"use client";

import { c as _c } from "react/compiler-runtime";
import { useT, useGameLocale, type TFn } from "@/lib/i18n";
import { useEffect, useState } from "react";
import { Link } from "@/i18n/navigation";
import dynamic from "next/dynamic";
import { cachedFetch } from "@/lib/fetch-cache";
import ScoreBadge, { scoreToTier } from "@/app/components/ScoreBadge";

// chart.js is ~70KB gzipped and the trends chart sits below the fold, so
// it must not ride in the first-load JS of every card/relic/potion page.
const EntityTrends = dynamic(() => import("./EntityTrends"), {
  ssr: false
});
import { CONTENT_BRACKETS, PLAYER_BRACKETS, combineBracket, splitBracket } from "@/lib/content-brackets";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
interface CharacterRow {
  character: string;
  picks: number;
  wins: number;
  win_rate: number;
}
interface BracketStat {
  picks: number;
  wins: number;
  win_rate: number;
  elo: number | null;
  score: number | null;
  total_runs: number;
  pick_rate: number;
  by_character?: CharacterRow[];
}
export interface EntityStats {
  entity_type: string;
  entity_id: string;
  picks: number;
  wins: number;
  win_rate: number;
  pick_rate: number;
  total_runs: number;
  baseline_win_rate: number;
  score: number | null;
  elo: number | null;
  brackets?: Record<string, BracketStat>;
  by_character: CharacterRow[];
  last_submitted_at: string | null;
  last_run_hash: string | null;
}
interface Props {
  entityType: "relics" | "cards" | "potions";
  entityId: string;
  /** Display name for the prose summary (e.g. "Sozu", "Strike"). */
  entityName: string;
  /** "wiki" swaps the tabbed styling for the unrolled card-page layout
   * (stat tiles + bracket pills + by-character bars). Data/fetch/bracket
   * logic is identical; only the presentation changes. */
  variant?: "default" | "wiki";
  /** Server-fetched stats used as the initial state so the numbers render into
   * the SSR HTML (crawlable) instead of a client-only "Loading" placeholder.
   * The component still re-fetches on mount to stay fresh. */
  initialStats?: EntityStats | null;
  /** Controlled bracket: when provided, the pills drive this value instead of
   * internal state, so a parent (e.g. the card page's infobox mini-stats) can
   * scope to the same bracket the user picked. Uncontrolled if omitted. */
  bracket?: string;
  onBracketChange?: (b: string) => void;
}

/** Compact 1.2k / 44.8k style number for the wiki tiles + bars. */
function kFmt(n: number): string {
  return n >= 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, "") + "k" : String(n);
}

/** Tier letter → accent color for the Codex Score tile badge. */
const TIER_COLOR: Record<string, string> = {
  S: "var(--gold)",
  A: "var(--good)",
  B: "var(--defect)",
  C: "#9aa4ab",
  D: "var(--regent)",
  F: "var(--warn)"
};

/** Character enum → bar color, using the global site character tokens so the
 * bars read the same regardless of which entity's --spine is set. */
const CHAR_COLOR: Record<string, string> = {
  SILENT: "var(--color-silent)",
  IRONCLAD: "var(--color-ironclad)",
  DEFECT: "var(--color-defect)",
  NECROBINDER: "var(--color-necrobinder)",
  REGENT: "var(--color-regent)"
};
function relativeTime(iso: string | null, t: TFn): string {
  if (!iso) return "—";
  // SQLite stores `YYYY-MM-DD HH:MM:SS` without TZ; treat as UTC.
  const safe = iso.includes("T") ? iso : iso.replace(" ", "T") + "Z";
  const ts = new Date(safe).getTime();
  if (Number.isNaN(ts)) return iso;
  const diffSec = Math.max(0, (Date.now() - ts) / 1000);
  const minutes = diffSec / 60;
  if (minutes < 1) return t("just now");
  if (minutes < 60) return t("{n}m ago", {
    n: Math.floor(minutes)
  });
  const hours = minutes / 60;
  if (hours < 24) return t("{n}h ago", {
    n: Math.floor(hours)
  });
  const days = hours / 24;
  if (days < 30) return t("{n}d ago", {
    n: Math.floor(days)
  });
  const months = days / 30;
  if (months < 12) return t("{n}mo ago", {
    n: Math.floor(months)
  });
  return t("{n}y ago", {
    n: Math.floor(months / 12)
  });
}
function characterPretty(c: string): string {
  // Names from the runs DB are uppercase enum values (IRONCLAD,
  // NECROBINDER). Title-case for display.
  if (!c) return "—";
  return c[0] + c.slice(1).toLowerCase();
}
function versionSortKey(v: string): number[] {
  return v.replace(/^v/, "").split(".").map(x => parseInt(x, 10) || 0);
}
function VersionScoreChart(t0) {
  const $ = _c(33);
  const {
    brackets
  } = t0;
  const t = useT();
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== brackets || $[1] !== t) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t10;
      if ($[11] !== brackets) {
        t10 = k_0 => ({
          version: k_0,
          ...brackets[k_0]
        });
        $[11] = brackets;
        $[12] = t10;
      } else {
        t10 = $[12];
      }
      const points = Object.keys(brackets).filter(k => /^v\d+(\.\d+)*$/.test(k) && brackets[k].score != null).sort(_temp).map(t10);
      if (points.length < 2) {
        t9 = null;
        break bb0;
      }
      const scores = points.map(_temp2);
      const min = Math.max(0, Math.min(...scores) - 5);
      const max = Math.min(100, Math.max(...scores) + 5);
      const span = max - min || 1;
      const step = 540 / (points.length - 1);
      let t11;
      if ($[13] !== min || $[14] !== span || $[15] !== step) {
        t11 = (p_0, i_0) => [10 + i_0 * step, 12 + (1 - ((p_0.score as number) - min) / span) * 62];
        $[13] = min;
        $[14] = span;
        $[15] = step;
        $[16] = t11;
      } else {
        t11 = $[16];
      }
      const xy = points.map(t11);
      const d_0 = xy.map(_temp3).join(" ");
      const labelEvery = Math.max(1, Math.ceil(points.length / 6));
      t7 = "pb-4 border-b border-[var(--border-subtle)]";
      let t12;
      if ($[17] !== t) {
        t12 = t("Score by patch");
        $[17] = t;
        $[18] = t12;
      } else {
        t12 = $[18];
      }
      if ($[19] !== t12) {
        t8 = <div className="text-xs text-[var(--text-muted)] font-semibold mb-1.5">{t12}</div>;
        $[19] = t12;
        $[20] = t8;
      } else {
        t8 = $[20];
      }
      t1 = "0 0 560 96";
      t2 = "w-full";
      t3 = "img";
      if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <line x1={10} y1={74} x2={550} y2={74} stroke="var(--border-subtle)" strokeWidth="1" />;
        $[21] = t4;
      } else {
        t4 = $[21];
      }
      t5 = <path d={d_0} fill="none" stroke="var(--accent-gold)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />;
      t6 = points.map((p_1, i_2) => <g key={p_1.version}><circle cx={xy[i_2][0]} cy={xy[i_2][1]} r="3" fill="var(--accent-gold)"><title>{`${p_1.version} · ${t("Codex Score")} ${p_1.score}` + (p_1.elo != null ? ` · Elo ${Math.round(p_1.elo)}` : "") + ` · ${p_1.win_rate}% ${t("win rate")} · ${p_1.picks.toLocaleString()} ${t("picks")}`}</title></circle><text x={xy[i_2][0]} y={xy[i_2][1] - 7} textAnchor="middle" fontSize="10" fill="var(--text-secondary)" className="tabular-nums">{p_1.score}</text>{(i_2 % labelEvery === 0 || i_2 === points.length - 1) && <text x={xy[i_2][0]} y={89} textAnchor={i_2 === 0 ? "start" : i_2 === points.length - 1 ? "end" : "middle"} fontSize="9" fill="var(--text-muted)">{p_1.version}</text>}</g>);
    }
    $[0] = brackets;
    $[1] = t;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
    t6 = $[7];
    t7 = $[8];
    t8 = $[9];
    t9 = $[10];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[22] !== t1 || $[23] !== t2 || $[24] !== t3 || $[25] !== t4 || $[26] !== t5 || $[27] !== t6) {
    t10 = <svg viewBox={t1} className={t2} role={t3}>{t4}{t5}{t6}</svg>;
    $[22] = t1;
    $[23] = t2;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t7 || $[31] !== t8) {
    t11 = <div className={t7}>{t8}{t10}</div>;
    $[29] = t10;
    $[30] = t7;
    $[31] = t8;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
}

/**
 * "Stats" tab content, community-run aggregates for one entity.
 * Layout: factual prose summary on top (doubles as SEO body content),
 * full per-character breakdown table below. Renders a graceful empty
 * state when the entity has no submitted runs yet so SEO crawlers
 * still see something other than spinners.
 */
function _temp3(t0, i_1) {
  const [x, y] = t0;
  return `${i_1 ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`;
}
function _temp2(p) {
  return p.score as number;
}
function _temp(a, b) {
  const ka = versionSortKey(a);
  const kb = versionSortKey(b);
  for (let i = 0; i < Math.max(ka.length, kb.length); i++) {
    const d = (ka[i] || 0) - (kb[i] || 0);
    if (d) {
      return d;
    }
  }
  return 0;
}
export default function EntityRunStats({
  entityType,
  entityId,
  entityName,
  variant = "default",
  initialStats = null,
  bracket,
  onBracketChange
}: Props) {
  const [stats, setStats] = useState<EntityStats | null>(initialStats);
  const [internalBracket, setInternalBracket] = useState("all");
  // Controlled when a parent passes `bracket`; internal otherwise.
  const selectedBracket = bracket ?? internalBracket;
  const setSelectedBracket = onBracketChange ?? setInternalBracket;
  const lang = useGameLocale();
  const t = useT();
  useEffect(() => {
    cachedFetch<EntityStats>(`${API}/api/runs/stats/${entityType}/${entityId}`).then(setStats);
  }, [entityType, entityId]);
  if (!stats) {
    return variant === "wiki" ? <p className="h-note">{t("Loading run stats…")}</p> : <p className="text-sm text-[var(--text-muted)]">
        {t("Loading run stats…")}
      </p>;
  }
  const empty = stats.picks === 0;
  const last = relativeTime(stats.last_submitted_at, t);

  // Bracket sub-menu: only the brackets with data for this entity. Selecting
  // one re-scopes the headline stats AND the per-character table below.
  const brackets = stats.brackets ?? {};
  const availableBrackets = CONTENT_BRACKETS.filter(b => brackets[b.key]);
  // Player counts are a second axis (like the metrics page): picking Solo with
  // A10 selected reads the "solo:a10" composite block. Only offered when the
  // API response carries player-count brackets (post-update snapshots).
  const availablePlayers = PLAYER_BRACKETS.filter(b_0 => brackets[b_0.key]);
  const {
    player: selPlayer,
    skill: selSkill,
    version: selVersion
  } = splitBracket(selectedBracket);
  const pickPlayer = (p: string) => setSelectedBracket(combineBracket(p, selSkill, selVersion));
  const pickSkill = (sk: string) => setSelectedBracket(combineBracket(selPlayer, sk === "all" ? "" : sk, selVersion));
  const sel = brackets[selectedBracket] ?? brackets["all"];
  // Everything below scopes to the selected bracket, with a fall back to the
  // global figures for a pre-update API response that lacks the per-bracket data.
  const selPickRate = sel?.pick_rate ?? stats.pick_rate;
  const selTotalRuns = sel?.total_runs ?? stats.total_runs;
  const selByChar = sel?.by_character ?? stats.by_character;
  const top = selByChar[0];
  const maxCharPicks = top?.picks ?? 0;
  const isAll = selectedBracket === "all";
  const selLabel = [availablePlayers.find(b_1 => b_1.key === selPlayer)?.label, CONTENT_BRACKETS.find(b_2 => b_2.key === (selSkill || "all"))?.label, selVersion || undefined].filter(Boolean).map(l => l && !/^v\d/.test(l) ? t(l) : l).join(" · ") || t("All");

  // ── Wiki layout: stat tiles + bracket pills + by-character bars. Same data,
  // same bracket handling as the default tabbed view above; only styling
  // differs. Styling comes from the .card-rvmp scoped CSS on the card page. ──
  if (variant === "wiki") {
    const tier = sel?.score != null ? scoreToTier(sel.score) : null;
    const wr = sel?.win_rate ?? stats.win_rate;
    const wins = sel?.wins ?? stats.wins;
    const picks = sel?.picks ?? stats.picks;
    const share = top && picks ? Math.round(top.picks / picks * 100) : 0;
    return <div>
        {empty ? <p className="h-note">
            {t("{name} hasn't appeared in any submitted community run yet (across {n} tracked).", {
          name: entityName,
          n: stats.total_runs.toLocaleString()
        })}{" "}
            <Link href="/leaderboards/submit">
              {t("Submit a run to seed this section.")}
            </Link>
          </p> : <>
            {availableBrackets.length > 1 && <div className="brkt" role="group" aria-label={t("Stats bracket")}>
                {availableBrackets.map(b_3 => <button key={b_3.key} type="button" className={`brkt-pill${(selSkill || "all") === b_3.key ? " on" : ""}`} onClick={() => pickSkill(b_3.key)}>
                    {t(b_3.label)}
                  </button>)}
              </div>}
            {availablePlayers.length > 0 && <div className="brkt" role="group" aria-label={t("Player count")}>
                <button type="button" className={`brkt-pill${selPlayer === "" ? " on" : ""}`} onClick={() => pickPlayer("")}>
                  {t("All players")}
                </button>
                {availablePlayers.map(b_4 => <button key={b_4.key} type="button" className={`brkt-pill${selPlayer === b_4.key ? " on" : ""}`} onClick={() => pickPlayer(b_4.key)}>
                    {t(b_4.label)}
                  </button>)}
              </div>}

            <div className="tiles">
              <div className="tile">
                <div className="k">{t("Win rate")}</div>
                <div className="v" style={{
              color: wr >= 50 ? "var(--good)" : "var(--warn)"
            }}>
                  {wr}
                  <span style={{
                fontSize: 16
              }}>%</span>
                </div>
                <div className="s">
                  {kFmt(wins)} {t("of")} {kFmt(picks)} {t("wins")}
                </div>
              </div>
              <div className="tile">
                <div className="k">{t("Pick rate")}</div>
                <div className="v">
                  {selPickRate}
                  <span style={{
                fontSize: 16
              }}>%</span>
                </div>
                <div className="s">
                  {picks.toLocaleString()} {t("picks")}
                </div>
              </div>
              <div className="tile">
                <div className="k">{t("Codex Score")}</div>
                <div className="v" style={{
              display: "flex",
              alignItems: "center",
              gap: 10
            }}>
                  {tier ? <>
                      <span className="tier" style={{
                  background: TIER_COLOR[tier.letter] ?? "var(--warn)"
                }}>
                        {tier.letter}
                      </span>
                      <span>{sel?.score}</span>
                    </> : <span>—</span>}
                </div>
                <div className="s">
                  {tier ? t(tier.label) : t("Not enough data")}
                </div>
              </div>
              <div className="tile">
                <div className="k">{t("Codex Elo")}</div>
                <div className="v">
                  {sel?.elo != null ? Math.round(sel.elo) : "—"}
                </div>
                <div className="s">{t("revealed preference")}</div>
              </div>
            </div>

            {selByChar.length > 0 && <>
                <h3 className="subh">
                  {t("Win rate by character")}
                  {!isAll ? ` · ${selLabel}` : ""}
                </h3>
                <div className="bars">
                  {selByChar.map(row => <div className="bar-row" key={row.character}>
                      <span className="name">
                        {characterPretty(row.character)}
                      </span>
                      <span className="bar-track">
                        <span className="bar-fill" style={{
                  width: `${row.win_rate}%`,
                  background: CHAR_COLOR[row.character] ?? "var(--text-2)"
                }} />
                      </span>
                      <span className="num">
                        <b>{row.win_rate}%</b> · {kFmt(row.picks)}
                      </span>
                    </div>)}
                </div>
              </>}

            {top && <p className="insight">
                {t("Most often taken by")}{" "}
                <b>{characterPretty(top.character)}</b> {t("players")}(
                {top.picks.toLocaleString()} {t("picks")} · {share}%{" "}
                {t("share")}).
                {tier ? <>
                    {" "}
                    {t("Codex rates it")} <b>{tier.letter}</b> (
                    {t(tier.label).toLowerCase()}) {t("overall")}.
                  </> : null}
              </p>}
            <EntityTrends entityType={entityType} entityId={entityId} bracket={selectedBracket} lang={lang} />
          </>}

        <p className="stat-note">
          {t("Community-submitted runs only, refreshed every 30 minutes.")}
          {selTotalRuns > 0 && <>
              {" "}
              {t("Pick rate is")} {selPickRate}% {t("of")}{" "}
              {selTotalRuns.toLocaleString()}
              {!isAll ? ` ${selLabel}` : ""} {t("tracked runs.")}
            </>}{" "}
          <Link href="/leaderboards/scoring">
            {t("How is the score calculated?")}
          </Link>
        </p>
      </div>;
  }
  return <div className="space-y-5">
      {/* Bracket sub-menu: re-scopes the headline stats below. Only shown when
          a bracket beyond "All" has data for this entity. */}
      {!empty && availableBrackets.length > 1 && <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-[var(--text-muted)] mr-1">
            {t("Bracket")}
          </span>
          {availableBrackets.map(b_5 => {
        const isActive = selectedBracket === b_5.key;
        return <button key={b_5.key} type="button" onClick={() => setSelectedBracket(b_5.key)} className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${isActive ? "bg-[var(--accent-gold)]/10 border-[var(--accent-gold)]/40 text-[var(--accent-gold)]" : "bg-[var(--bg-card)] border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--border-accent)]"}`}>
                {t(b_5.label)}
              </button>;
      })}
        </div>}

      {/* Codex Score hero for the selected bracket: 0-100 score badge plus the
          win rate with Codex Elo right beside it, and picks. Bayesian-shrunk so
          low-N entities sit near neutral. See `_compute_score`. */}
      {sel && sel.score != null && <div className="flex items-center gap-3 pb-4 border-b border-[var(--border-subtle)]">
          <ScoreBadge score={sel.score} size="lg" showNumber />
          <div className="text-xs text-[var(--text-muted)] leading-snug">
            <div className="text-[var(--text-secondary)] font-semibold mb-0.5">
              {t("Codex Score")}
              {selectedBracket !== "all" ? ` · ${selLabel}` : ""}
            </div>
            <div>
              <strong className="text-[var(--text-secondary)]">
                {sel.win_rate}%
              </strong>{" "}
              {t("win rate")}
              {sel.elo != null && <>
                  {" · "}
                  <strong className="text-[var(--text-secondary)]">
                    {Math.round(sel.elo)}
                  </strong>{" "}
                  {t("Elo")}
                </>}
              {" · "}
              {sel.picks.toLocaleString()} {t("picks")}
              {" · "}
              <Link href="/leaderboards/scoring" className="text-[var(--accent-gold)]/80 hover:text-[var(--accent-gold)] hover:underline">
                {t("how is this calculated?")}
              </Link>
            </div>
          </div>
        </div>}

      {!empty && <VersionScoreChart brackets={brackets} lang={lang} />}

      {/* Prose summary, also serves as crawlable SEO body content. */}
      <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
        {empty ? <>
            {t("{name} hasn't appeared in any submitted community run yet (across {n} total runs tracked).", {
          name: entityName,
          n: stats.total_runs.toLocaleString()
        })}{" "}
            <Link href="/leaderboards/submit" className="text-[var(--accent-gold)] hover:underline">
              {t("Submit a run that includes it to seed this section.")}
            </Link>
          </> : <>
            <strong className="text-[var(--text-primary)]">
              {sel.win_rate}%
            </strong>{" "}
            {t("win rate across")} <strong>{sel.picks.toLocaleString()}</strong>{" "}
            {t("picks")}
            {top && <>
                . {t("Most often taken by")}{" "}
                <strong className="text-[var(--text-primary)]">
                  {characterPretty(top.character)}
                </strong>{" "}
                {t("players")} ({top.picks.toLocaleString()} {t("picks")} ·{" "}
                {Math.round(top.picks / sel.picks * 100)}% {t("share")})
              </>}
            . {t("Last picked")} <strong>{last}</strong>
            {stats.last_run_hash && <>
                {" "}
                {t("in run")}{" "}
                <Link
          // Frontend route is /runs/<hash>; the /shared/ segment
          // exists only on the backend API (/api/runs/shared/<hash>)
          // and was an early copy-paste mistake here.
          href={`/runs/${stats.last_run_hash}`} className="text-[var(--accent-gold)] hover:underline font-mono text-xs">
                  #{stats.last_run_hash.slice(0, 8)}
                </Link>
              </>}
            .
          </>}
      </p>

      {/* Per-character breakdown table, hidden when empty. */}
      {!empty && selByChar.length > 0 && <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-2">
            {t("Picks by character")}
            {!isAll ? ` · ${selLabel}` : ""}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-[var(--text-muted)] border-b border-[var(--border-subtle)]">
                  <th className="text-left py-2 pr-3 font-semibold">
                    {t("Character")}
                  </th>
                  <th className="text-right py-2 px-3 font-semibold">
                    {t("Picks")}
                  </th>
                  <th className="text-right py-2 px-3 font-semibold">
                    {t("Win Rate")}
                  </th>
                  <th className="text-left py-2 pl-3 font-semibold w-1/3">
                    {t("Distribution")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {selByChar.map(row_0 => {
              const pct = maxCharPicks ? row_0.picks / maxCharPicks * 100 : 0;
              return <tr key={row_0.character} className="border-b border-[var(--border-subtle)] last:border-b-0">
                      <td className="py-2 pr-3 text-[var(--text-secondary)]">
                        {characterPretty(row_0.character)}
                      </td>
                      <td className="py-2 px-3 text-right text-[var(--text-secondary)] font-mono tabular-nums">
                        {row_0.picks.toLocaleString()}
                      </td>
                      <td className="py-2 px-3 text-right text-[var(--text-secondary)] font-mono tabular-nums">
                        {row_0.win_rate}%
                      </td>
                      <td className="py-2 pl-3">
                        <div className="h-1.5 w-full rounded-full bg-[var(--bg-primary)]">
                          <div className="h-1.5 rounded-full bg-[var(--accent-gold)]/60" style={{
                      width: `${pct}%`
                    }} />
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>
        </div>}

      <p className="text-xs text-[var(--text-muted)]">
        {t("Stats reflect community-submitted runs only and refresh every 30 minutes.")}
        {selTotalRuns > 0 && <>
            {" "}
            {t("Pick rate:")} <strong>{selPickRate}%</strong> {t("of")}{" "}
            {selTotalRuns.toLocaleString()}
            {!isAll ? ` ${selLabel}` : ""} {t("tracked runs.")}
          </>}
      </p>
    </div>;
}